/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2020, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { Logger } from '@core/lib/services';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { appConstants } from '@app/common/app-constants';
import { UtilService } from '@app/util/util.service';
import { AppConfigService } from '@app/common/app-config.service';
import { IntegrationConfig } from '@app/di/data-model/di-fhircast-config';

const log = new Logger('DiConfigService');

@Injectable({
  providedIn: 'root'
})
export class DiConfigService {
  config: any;
  uvSession: any;
  encryptedPassword: any = undefined;
  encryptionKey: any = undefined;
  isURLLauncherConfigValidated = false;

  REPORTING_CONFIG;
  DI_CONFIG;
  UV_PREFERENCE_CONFIG;
  APP_LAUNCHER_CONFIG;
  WFM_CONTEXT_CONFIG;
  urlLauncherObj: any;

  // Viewer configuration
  UVIEWER_CONFIG;
  VIEWER_CONTEXT_CONFIG;

  // Integration config for fhircast and ev
  private INTEGRATION_CONFIG: IntegrationConfig | null = null;

  constructor(
    private dataService: ProxyDataService,
    private utilService: UtilService,
    private appConfigService: AppConfigService
  ) {
    this.uvSession = {};
    this.urlLauncherObj = {};
  }

  initData(data: any) {
    this.config = data;
    this.prepareConfigData();
  }

  getOtherConfigurations() {
    // For Cardio workflow, there is no password available - authentication done by jwtToken
    if (!this.utilService.externalLoginApp) {
      this.createConfigKey();
    }
  }

  getData() {
    return new Promise((resolve, reject) => {
      if (this.config) {
        this.getOtherConfigurations();
        resolve(this.config);
      } else {
        this.dataService.httpGet(appConstants.appURLs.desktopIntegration).then(
          data => {
            this.initData(data);
            this.getOtherConfigurations();
            resolve(data);
          },
          error => {
            log.error(`getData - error occurred while getting desktop integration config,
          ERROR - ${JSON.stringify(error)}`);
            reject(error);
          }
        );
      }
    });
  }

  getServiceValues(url, params = {}) {
    if (!_.isEmpty(params)) {
      return this.dataService.httpPost(url, params);
    }
    return this.dataService.httpGet(url);
  }

  getJwtToken(): Promise<any> {
    log.info('Desktop Integration Config - getJwtToken() invoked');
    const httpOptions = {
      responseType: 'text',
      headers: new HttpHeaders({
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache'
      })
    };
    return this.dataService.httpGet(appConstants.appURLs.jwtToken, httpOptions);
  }

  private prepareConfigData() {
    // get queryparam and see if reportType='ps' set this.config.reportingIntegrationType=PowerScribe
    // this.config.modulename to PSPlugin
    // if reportType is 'gr' set this.config.reportingIntegrationType= GenericPlugin 
    // and this.config.modulename to ReportingPlugin else if reportingType is sr 
    // set this.config.reportingIntegrationType to NativeReporting else reportType is none 
    // set this.config.reportingIntegrationType to None else do not make any change to reportingIntegrationType and modulename
      const reportType = this.utilService.getQueryParam('reportType');
      if(reportType === 'ps') {
        log.info('prepareConfigData - reportType is PS, setting reportingIntegrationType to PowerScribe and modulename to PSPlugin');
        this.config.reportingintegrationtype = appConstants.reportingAppName.PS;
        this.config.reportingmodulename = 'PSPlugin';
      } else if(reportType === 'gr') {
        log.info('prepareConfigData - reportType is GR, setting reportingIntegrationType to Generic and modulename to ReportingPlugin');
        this.config.reportingintegrationtype = appConstants.reportingAppName.Generic;
        this.config.reportingmodulename = 'ReportingPlugin';
      } else if(reportType === 'sr') {
        log.info('prepareConfigData - reportType is SR, setting reportingIntegrationType to NativeReporting and modulename to ReportingPlugin');
        this.config.reportingintegrationtype = appConstants.reportingAppName.NPR;
      } else if(reportType === 'none') {
        log.info('prepareConfigData - reportType is None, setting reportingIntegrationType to None');
        this.config.reportingintegrationtype = appConstants.reportingAppName.None;
      } else {
        log.info('prepareConfigData - reportType is not specified or unrecognized, keeping existing reportingIntegrationType and modulename');
      }
      

    if (!_.isUndefined(this.config)) {
      this.UVIEWER_CONFIG = {
        OWNERINSTANCE: this.config.ownerinstance,
        MODULENAME: this.config.modulename,
        LANGUAGE: this.config.language,
        NOWAITDLG: this.config.nowaitdlg,
        REQUEST_PRIORS: this.config.requestPriors,
        UNFOCUSED: this.config.unfocused,
        LOGIN: this.config.login,
        SERVER: this.config.server,
        PORT: this.config.port,
        OWNER_TYPE: this.config.ownerType,
        PLUGIN_MANAGER: this.config.pluginManager,
        LS_PLUGINLOAD_TIMEOUT: UtilService.parseInt(this.config.lspluginloadtimeout, 15000),
        SETCONTEXT_TIMEOUT: UtilService.parseInt(this.config.viewersetcontexttimeout, 60000),
        PLUGIN_CLOSE_TIMEOUT: UtilService.parseInt(this.config.pluginCloseTimeout, 30000),
        UVCLOSE_ACTS_LIKE_REPORT_SAVED: this.config.uvCloseActsLikeReportSaved,
        SUBSCRIBE_SOURCE: this.config.subscribeSource,
        SUBSCRIBE_TOPIC: this.config.subscribeTopic,
        SECURITY: this.config.security
      };
      this.REPORTING_CONFIG = {
        OWNERINSTANCE: this.config.ownerinstance,
        MODULENAME: this.config.reportingmodulename + '_' + this.config.wfmpluginsversion,
        USERNAME: this.config.login,
        SITENAME: this.config.reportingsitename,
        SITENAME_KEY_LOCATION: this.config.reportingsitenamekeylocation,
        SINGLESITE: this.config.reportingsinglesite,
        OWNER_TYPE: this.config.ownerType,
        SETCONTEXT_TIMEOUT: this.config.reportingsetcontexttimeout,
        SITENAMEMAP: this.config.sitenamemap,
        ASSOCIATION_ENABLED: this.config.reportingAssociationEnabled === 'true',
        LOGOUT_TIMEOUT: this.config.reportinglogouttimeout,
        PROCESS_NAME: this.config.reportingappprocessname,
        FOCUS: this.config.psfocus,
        APPPATH: this.config.reportingapppath,
        CUSTOMOPTIONS: this.config.customoptions,
        LOGININTERVAL: this.config.logininterval,
        SENDPASSWORD: this.config.psSendPassword,
        SSOSENDUSERNAME: this.config.psSsoSendUsername
      };
      this.DI_CONFIG = {
        ORGANIZATIONMAP: this.config.organizationmap,
        REPORTING_INTEGRATION_TYPE: !!this.utilService.externalLoginApp
          ? appConstants.reportingAppName.None
          : this.config.reportingintegrationtype,
        WFMPLUGINS_VERSION: this.config.wfmpluginsversion,
        WFMPCONTEXTLUGIN_VERSION: this.config.wfmcontextpluginversion,
        FORCE_CLOSE_TIMEOUT: {
          report: this.config.reportingsetcontexttimeout,
          viewer: this.config.viewersetcontexttimeout
        }
      };
      this.VIEWER_CONTEXT_CONFIG = {
        MODULENAME: this.config.viewercontextmodulename,
        OWNERINSTANCE: this.config.ownerinstance,
        UV_SYNC: this.config.uvSync
      };
      this.UV_PREFERENCE_CONFIG = {
        uvPreferenceWindowWidth: this.config.uvPreferenceWindowWidth,
        uvPreferenceWindowHeight: this.config.uvPreferenceWindowHeight,
        uvPreferenceWindowLeft: this.config.uvPreferenceWindowLeft,
        uvPreferenceWindowTop: this.config.uvPreferenceWindowTop
      };
      this.APP_LAUNCHER_CONFIG = {
        OWNERINSTANCE: this.config.ownerinstance,
        MODULENAME: this.config.appLauncherModulename + '_' + this.config.wfmpluginsversion,
        PLUGIN_MANAGER: this.config.pluginManager,
        PROCESSNAME: this.config.appLauncherProcessName,
        SETCONTEXT_TIMEOUT: this.config.appLauncherSetcontextTimeout,
        MIVPROCESSNAME: this.config.appLauncherMivProcessName
      };
      this.WFM_CONTEXT_CONFIG = {
        OWNERINSTANCE: this.config.ownerinstance,
        MODULENAME: this.config.wfmContextModuleName
      };
    }
  }

  getDiConfig() {
    return this.DI_CONFIG;
  }

  getReportingConfig() {
    return this.REPORTING_CONFIG;
  }

  getViewerConfig() {
    return this.UVIEWER_CONFIG;
  }

  getPSReportingIntegrationName() {
    return appConstants.reportingAppName.PS;
  }

  getGenericReportingIntegrationName() {
    return appConstants.reportingAppName.Generic;
  }

  getNoReportingIntegrationConfigName() {
    return appConstants.reportingAppName.None;
  }

  getEncryptedPassword() {
    log.info('Desktop Integration Config - getEncryptedPassword() invoked');
    if (
      this.encryptedPassword === undefined &&
      (this.utilService.getSystemProp()['SSO_Flag'] === undefined ||
        this.utilService.getSystemProp()['SSO_Flag'] === 'false')
    ) {
      (async () => {
        try {
          const httpOptions = { INTEGRATION_SESSION_ID: this.encryptionKey };
          this.encryptedPassword = await this.getServiceValues(
            appConstants.appURLs.encryptedPassword,
            this.encryptionKey !== undefined ? httpOptions : undefined
          );
        } catch (err) {
          log.error('Error occurred while getting EncryptedPassword, Error -', JSON.stringify(err));
        }
      })();
    }
    return this.encryptedPassword;
  }

  createConfigKey() {
    if (this.encryptionKey === undefined) {
      (async () => {
        try {
          const response = await this.getServiceValues(appConstants.appURLs.integrationSecret);
          this.encryptionKey = response['INTEGRATION_SESSION_ID'];
          this.getEncryptedPassword();
        } catch (err) {
          log.error('Error occurred while getting EncryptedPassword, Error -', JSON.stringify(err));
        }
      })();
    }
    return this.encryptionKey;
  }

  getOrganizationMap() {
    this.dataService.get('config/organization-details').then(
      data => {
        this.config.organizationmap = data;
        this.DI_CONFIG.ORGANIZATIONMAP = data;
        log.info(
          'Desktop Integration Config - getOrganizationMap - Received : ' + JSON.stringify(this.config.organizationmap)
        );
      },
      reason => {
        // error in retrieve organization map
        log.error(
          'Desktop Integration Config - getOrganizationMap() : Error occurred while receiving ' +
            'organization map data. Error: ' +
            JSON.stringify(reason)
        );
      }
    );
  }

  getMultiSiteConfiguration() {
    const reportingConfig = this.REPORTING_CONFIG;
    if (reportingConfig.SINGLESITE === 'false') {
      return new Promise<void>((resolve, reject) => {
        this.dataService.get('config/ps-site-name-mapping').then(
          (data: any) => {
            this.config.sitenamemap = data.pssitenamemapping !== undefined ? data.pssitenamemapping : undefined;
            resolve();
            log.info('Desktop Integration Config - siteName map retrieved');
          },
          reason => {
            // error in retrieve siteName map
            log.error(
              'Desktop Integration Config - getMultiSiteConfiguration() : Error occurred while ' +
                'receiving sitenamemapping data Error=> ' +
                JSON.stringify(reason)
            );
            reject();
          }
        );
        const keyLocationArray = reportingConfig.SITENAME_KEY_LOCATION.split(';');
        if (keyLocationArray.length === 3) {
          this.getOrganizationMap();
        } else if (keyLocationArray.length === 1) {
          log.info('Desktop Integration Config - Only siteName map is required');
        } else {
          log.error('Desktop Integration Config - getMultiSiteConfiguration() : ' + 'Invalid Site name key location');
        }
      });
    } else {
      // confirm it is a single site
      log.info('Desktop Integration Config - confirm it is a single site');
    }
  }

  getURLLauncherIntegrations() {
    return this.config.urlIntegrations;
  }

  getURLLauncherConfig() {
    this.urlLauncherObj['TITLE'] = this.config.urlLauncherDisplayTitle;
    this.urlLauncherObj['urlLauncherEncryptionMode'] = this.config.urlLauncherEncryptionLibrary;
    this.urlLauncherObj['urlLauncherEncryptionSecret'] = this.config.urlLauncherEncryptionSecret;
    this.urlLauncherObj['urlLauncherWindowWidth'] = this.config.urlLauncherWindowWidth;
    this.urlLauncherObj['urlLauncherWindowHeight'] = this.config.urlLauncherWindowHeight;

    // Validate URL launcher integrations once, if there any invalid integration remote it from the list and
    // set NOTIFY_USER_INVALID_INTEGRATION TRUE
    if (!this.isURLLauncherConfigValidated) {
      const urlIntegrations = this.getURLLauncherIntegrations();
      this.isURLLauncherConfigValidated = true;
      this.config.urlLauncherEnabled = false;
      log.info(
        'Desktop Integration Config - getURLLauncherConfig, before validation  : ' + JSON.stringify(urlIntegrations)
      );

      // Check whether at least one URL integrations is defined
      const isURLIntegrationsPresent =
        !(_.isUndefined(urlIntegrations) || _.isEmpty(urlIntegrations)) &&
        !(_.isUndefined(urlIntegrations.URLIntegrations) || _.isEmpty(urlIntegrations.URLIntegrations)) &&
        !(
          _.isUndefined(urlIntegrations.URLIntegrations.URLIntegration) ||
          _.isEmpty(urlIntegrations.URLIntegrations.URLIntegration)
        );

      log.info(
        'Desktop Integration Config - getURLLauncherConfig, isURLIntegrationsPresent : ' + isURLIntegrationsPresent
      );
      if (isURLIntegrationsPresent) {
        // If there is an array of integrations use angular.forEach to iterate the list, else treat this as
        // an object
        if (Array.isArray(urlIntegrations.URLIntegrations.URLIntegration)) {
          for (let index = 0; index < urlIntegrations.URLIntegrations.URLIntegration.length; ) {
            // Check for invalid integration
            if (
              _.isUndefined(urlIntegrations.URLIntegrations.URLIntegration[index].DisplayName) ||
              _.isEmpty(urlIntegrations.URLIntegrations.URLIntegration[index].DisplayName) ||
              _.isUndefined(urlIntegrations.URLIntegrations.URLIntegration[index].URL) ||
              _.isEmpty(urlIntegrations.URLIntegrations.URLIntegration[index].URL) ||
              !this.isValidUrl(urlIntegrations.URLIntegrations.URLIntegration[index])
            ) {
              log.info(
                'Desktop Integration Config - getURLLauncherConfig, invalid integration : ' +
                  JSON.stringify(urlIntegrations.URLIntegrations.URLIntegration[index])
              );
              urlIntegrations.URLIntegrations.URLIntegration.splice(index, 1);
            } else {
              index++;
            }
          }
          this.config.urlLauncherEnabled = !(
            _.isUndefined(urlIntegrations.URLIntegrations.URLIntegration.length) ||
            urlIntegrations.URLIntegrations.URLIntegration.length === 0
          );
        } else {
          // Single integration present and if it is invalid disabled the URL Launcher
          if (
            _.isUndefined(urlIntegrations.URLIntegrations.URLIntegration.DisplayName) ||
            _.isEmpty(urlIntegrations.URLIntegrations.URLIntegration.DisplayName) ||
            _.isUndefined(urlIntegrations.URLIntegrations.URLIntegration.URL) ||
            _.isEmpty(urlIntegrations.URLIntegrations.URLIntegration.URL) ||
            !this.isValidUrl(urlIntegrations.URLIntegrations.URLIntegration)
          ) {
            log.info(
              'Desktop Integration Config - getURLLauncherConfig, invalid integration : ' +
                JSON.stringify(urlIntegrations.URLIntegrations.URLIntegration)
            );
            this.config.urlLauncherEnabled = false;
          } else {
            this.config.urlLauncherEnabled = true;
          }
        }
      }
      log.info(
        'Desktop Integration Config - getURLLauncherConfig, after validation : ' + JSON.stringify(urlIntegrations)
      );
      if (!this.config.urlLauncherEnabled) {
        log.error(
          'Desktop Integration Config - getURLLauncherConfig, ' +
            'There is no valid URL integration so URL Launcher is disabled.'
        );
      }
    }
    this.urlLauncherObj['ENABLED'] = this.config.urlLauncherEnabled;
    this.urlLauncherObj['URL_INTEGRATIONS'] = this.config.urlIntegrations;
  }

  isValidUrl(urlIntegration) {
    if (!_.isUndefined(urlIntegration.URL) && !_.isEmpty(urlIntegration.URL)) {
      const regex = [
        '^((https?:\\/\\/)(([w]{3}\\.){0,1})',
        '(([-a-z0-9\\.]+)|((?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|',
        '[01]?[0-9][0-9]?)){1})',
        '(:([0-9]{1,4}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5])){0,1}(\\/.*){0,1})$'
      ].join('');

      const httpCheck = new RegExp(regex, 'gi').test(urlIntegration.URL.trim());
      if (httpCheck) {
        return true;
      } else {
        log.error(
          'Desktop Integration Config - isValidUrl, invalid url for the following site : ' + urlIntegration.DisplayName
        );
        return false;
      }
    }
  }

  getXmlTemplates() {
    log.info('Desktop Integration Config - getXmlTemplates(): START');
    return new Promise((resolve, reject) => {
      this.dataService.httpGet(appConstants.appURLs.xmlTemplates).then(
        data => {
          log.debug(
            'Desktop Integration Config - getXmlTemplates() - promise received, values: ' + JSON.stringify(data)
          );
          resolve(data);
        },
        reason => {
          log.error(
            'Desktop Integration Config - getXmlTemplates() : Error occurred while receiving the xml' +
              ' templates. Error: ' +
              JSON.stringify(reason)
          );
          reject(reason);
        }
      );
      log.info('Desktop Integration Config - getXmlTemplates(): END');
    });
  }

  readAWConfiguration() {
    return new Promise((resolve, reject) => {
      this.dataService.get('config/awConfiguration').then(
        (data: any) => {
          log.info('Desktop Integration Config - readAWConfiguration, result : ' + JSON.stringify(data));
          const result = new Map<any, any>();
          if (data) {
            result.set('awIntegrationStatus', data.IntegrationStatus === 'true');
            result.set('awSecurity', data.Security === 'true');
          }
          resolve(result);
        },
        reason => {
          log.error(
            'Desktop Integration Config - readAWConfiguration, error occurred while receiving the ' +
              'AW configuration : ' +
              JSON.stringify(reason)
          );
          reject(reason);
        }
      );
    });
  }

  getUVPreferenceConfig() {
    return this.UV_PREFERENCE_CONFIG;
  }

  getAppLauncherConfig() {
    return this.APP_LAUNCHER_CONFIG;
  }

  isAssociationEnabled() {
    // Based on the configuration and Patient History availability
    return this.REPORTING_CONFIG.ASSOCIATION_ENABLED && this.utilService.isAssociationEnabled();
  }

  loadFHIRCastIntegrationConfig() {
    this.INTEGRATION_CONFIG = this.appConfigService.getfhirCastIntegrationConfig();
    console.log('[DA-DEBUG] DiConfigService.loadFHIRCastIntegrationConfig() — INTEGRATION_CONFIG:', this.INTEGRATION_CONFIG ? JSON.stringify(this.INTEGRATION_CONFIG) : 'NULL');
    console.log('[DA-DEBUG] DiConfigService.loadFHIRCastIntegrationConfig() — Da config present:', !!this.INTEGRATION_CONFIG?.Da);
    log.info(`FHIRCast config loaded: Da=${!!this.INTEGRATION_CONFIG?.Da}, Ev=${!!this.INTEGRATION_CONFIG?.Ev}`);
  }

  get integrationConfig(): IntegrationConfig | null {
    return this.INTEGRATION_CONFIG ? this.INTEGRATION_CONFIG : null;
  }

  isEvConfigured(): boolean {
    return !!this.integrationConfig?.Ev;
  }

  get preferredArchive() {
    return this.appConfigService.getAppConfigBySubCategory(appConstants.configSubCategory.PREFERRED_ARCHIVE);
  }
}
